import { memo, useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  DocumentViewer,
  MiButton,
  MiDetailFields,
  MiIcon,
  MiModalConfirm,
} from "@miplatform/components";
import {
  ATTEMPT_STATUSES,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  DATE_TIME_FORMATS,
  ICONS_WEB,
  INPUT_TYPES, // PLATFORM_MODULES,
  ISSUE_TYPES,
} from "@miplatform/constants";
import { useBoolean, useEdit } from "@miplatform/hooks";
import { Colors } from "@miplatform/types";
import { getUniqueObjects, toFormattedDate } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Box } from "@mui/material";
import { AreaAutoComplete } from "../../../../../../../components/AreaAutoComplete/AreaAutoComplete";
import {
  CLOSEOUT_SCREENS as SCREENS,
  CLOSING_OUT_VIEWS as VIEWS,
} from "../../../constants";
import { IssueHistoryHeader } from "../IssueHistoryHeader";
import { IssueUsersHeader } from "../IssueUsersHeader";
import { IssueReviewContent } from "./IssueReviewContent";
import { useClosingoutViewModel } from "./useClosingoutViewModel";

const SECTIONS = {
  DIAGNOSED_ISSUE: "Technicians Diagnostics (Before Repair Attempt)",
  REPAIR_DETAILS: "Repair Details (After Repair Attempt)",
  TASK_DESCRIPTION: "Task Description",
  RESOLUTION: "Resolution",
  TASK_DETAILS: "Task Details",
};

const INITIAL_EDIT = {
  issueDescription: "",
  repairDetails: "",
  repairStatus: "",
  diagnosedIssue: "",
};

const AugmentedOption = ({ icon, color, text }) => (
  <Box display="flex" alignItems="center">
    <MiIcon sx={{ marginRight: 1 }} size={1} path={icon} color={color} />
    <Box sx={{ width: "80%", marginLeft: "4px" }}>{text}</Box>
  </Box>
);

export const Closingout = memo(
  ({
    issueIndex,
    readOnly = false,
    setIssueIndex,
    attempt = {},
    selectedIssue = null,
    setActiveScreen,
    setIsWoReviewed = () => {},
    setSelectedIssue = () => {},
    view: initialView = VIEWS.ISSUE_REVIEW,
    getAttempt,
    onCancel = () => null,
    isLoading,
    users = [],
  }) => {
    const [completedIssueCount, setCompletedIssueCount] = useState(0);
    const [initialEdit, setInitialEdit] = useState(INITIAL_EDIT);
    const [view, setView] = useState(initialView);
    const [selectedIssueResult, setSelectedIssueResult] = useState();
    const [confirmModalConfig, setConfirmModalConfig] = useState({
      open: false,
      title: "",
      titleIcon: null,
      description: "",
      textPositive: BUTTON_LABELS.CONFIRM,
      textNegative: BUTTON_LABELS.CANCEL,
      handlePositive: () => {},
      handleNegative: () =>
        setConfirmModalConfig((prev) => ({ ...prev, open: false })),
    });

    const { issues = [] } = attempt;
    const { classes, theme } = useStyles({
      history: attempt.attemptNumber > 1,
    });
    const issueCount = issues.length || 0;
    const isLastOne = issueIndex + 1 === issueCount;

    const currentIssue = useMemo(
      () => selectedIssue || issues[issueIndex] || {},
      [issues, selectedIssue, issueIndex]
    );

    const { title = "--", originalDescription: description = "--" } =
      currentIssue.issueResult || currentIssue || {};
    const [
      isIssueImageViewer,
      turnOnIssueImageViewer,
      turnOffIssueImageViewer,
    ] = useBoolean();
    const {
      ISSUE_RESOLUTION_OPTIONS,
      COMPLETED_STATUSES,
      issueImages,
      repairImages,
      showImagesViewer,
      imagesInViewer,
      issueTemplates,
      setImagesInViewer,
      setShowImagesViewer,
      setIssueImages,
      setRepairImages,
      handleAddIssueImage,
      handleAddRepairImage,
      removeIssueImage,
      removeRepairImage,
      reviewIssue,
      filterIssueFiles,
      isStatusRepair,
      getTaskResolutionStatus,
      getIssueTemplates,
      getIssue,
      issue: issueWithHistory,
    } = useClosingoutViewModel({
      orderTemplateId:
        attempt.orderTemplateId || attempt.assignment?.order?.orderTemplateId,
      getAttempt,
    });

    const edit = useEdit(initialEdit);
    const isTask = currentIssue.type === ISSUE_TYPES.TASK;
    const isAttemptOpen = ![
      ATTEMPT_STATUSES.CLOSEOUT_IN_PROGRESS,
      ATTEMPT_STATUSES.CLOSED,
    ].includes(attempt.attemptStatus);

    useEffect(() => {
      edit.reset();

      const { resolvingDocuments, originatedDocuments } = filterIssueFiles(
        getUniqueObjects([
          ...(currentIssue.issueResult?.documents || []),
          ...(currentIssue.documents || []),
        ])
      );

      setIssueImages(originatedDocuments);
      setRepairImages(resolvingDocuments);

      const {
        description: issueDescription = "",
        repairDescription: repairDetails = "",
        originalDescription = "",
        areaName = "",
        issueTemplateId = "",
        diagnosedIssue = "",
      } = currentIssue.issueResult || {};

      setInitialEdit({
        issueDescription,
        repairDetails,
        repairStatus: getTaskResolutionStatus(currentIssue.issueResult || {}),
        diagnosedIssue,
        issueTemplateId:
          issueTemplateId || currentIssue.issueTemplateId || null,
        areaName: areaName || currentIssue.areaName || "",
        originalDescription:
          originalDescription || currentIssue.originalDescription || "",
      });

      getIssue({ id: currentIssue.id });
    }, [issueIndex]);

    const allFields = [
      {
        label: "Diagnosed Task",
        name: "diagnosedIssue",
        required: !isAttemptOpen,
        fieldType: INPUT_TYPES.SELECT,
        options: issueTemplates.map((x) => ({ label: x.name, value: x.name })),
        width: "100%",
        style: { paddingLeft: 0, paddingRight: 0 },
      },
      {
        label: "Task Description",
        name: "issueDescription",
        required: !isAttemptOpen,
        width: "100%",
        multiline: true,
        rows: 3,
        style: { paddingLeft: 0, paddingRight: 0 },
      },
      {
        label: "Repair Details",
        name: "repairDetails",
        required: !isAttemptOpen,
        width: "100%",
        multiline: true,
        rows: 3,
        style: { paddingLeft: 0, paddingRight: 0 },
      },
      {
        label: "Repair and Visit Status?",
        name: "repairStatus",
        required: !isAttemptOpen,
        fieldType: INPUT_TYPES.SELECT,
        options: Object.keys(ISSUE_RESOLUTION_OPTIONS).map((optionKey) => ({
          label: <AugmentedOption {...ISSUE_RESOLUTION_OPTIONS[optionKey]} />,
          value: ISSUE_RESOLUTION_OPTIONS[optionKey].value,
        })),
        style: {
          paddingLeft: 0,
          paddingRight: 0,
          "& .MuiInputLabelRoot": {
            color: isStatusRepair(edit.getValue("repairStatus"))
              ? "primary"
              : "red",
          },
        },
        InputProps: {
          error: !isStatusRepair(edit.getValue("repairStatus")),
        },
        disabled:
          isAttemptOpen || ATTEMPT_STATUSES.CLOSED === attempt.attemptStatus,
      },
      {
        label: "Task Title",
        name: "issueTemplateId",
        fieldType: INPUT_TYPES.SELECT,
        options: issueTemplates.map((x) => ({ label: x.name, value: x.id })),
        style: isTask ? { display: "none" } : null,
        padding: theme.spacing(1, 1, 0, 0),
        required: true,
        readOnly: isTask,
      },
      {
        label: "Area",
        name: "areaName",
        fieldType: INPUT_TYPES.CUSTOM,
        customComponent: (
          <AreaAutoComplete
            onChange={(value) => {
              edit.update({ areaName: value });
            }}
            value={edit.getValue("areaName") || initialEdit.areaName}
            required={!isTask}
          />
        ),
        style: { paddingLeft: 0, paddingRight: 0 },
      },
      {
        label: "Description",
        name: "originalDescription",
        multiline: true,
        required: true,
        rows: 2,
        width: "100%",
        padding: theme.spacing(1, 0, 2, 0),
      },
    ];

    const getFields = ({ section, disabled }) => {
      switch (section) {
        case SECTIONS.DIAGNOSED_ISSUE:
          return allFields
            .slice(0, 1)
            .map((x) => ({ ...x, disabled: disabled || x.disabled }));
        case SECTIONS.TASK_DESCRIPTION:
          return allFields
            .slice(1, 2)
            .map((x) => ({ ...x, disabled: disabled || x.disabled }));
        case SECTIONS.REPAIR_DETAILS:
          return allFields
            .slice(2, 3)
            .map((x) => ({ ...x, disabled: disabled || x.disabled }));
        case SECTIONS.RESOLUTION:
          return allFields
            .slice(3, 4)
            .map((x) => ({ ...x, disabled: disabled || x.disabled }));
        case SECTIONS.TASK_DETAILS:
          return allFields
            .slice(4, 7)
            .map((x) => ({ ...x, disabled: disabled || x.disabled }));
        default:
          return [];
      }
    };

    const isFormValid = () => {
      if (selectedIssueResult) {
        return true;
      }

      if (selectedIssue && isAttemptOpen) {
        if (isTask) {
          return !edit.allFilled("originalDescription");
        }
        return !edit.allFilled(
          "issueTemplateId",
          "areaName",
          "originalDescription"
        );
      }

      if (
        edit.getValue("repairStatus") ===
        ISSUE_RESOLUTION_OPTIONS.REPAIRED.value
      ) {
        if (isTask) return !edit.allFilled("issueDescription", "repairStatus");
        return !edit.allFilled(
          "issueDescription",
          "repairDetails",
          "repairStatus",
          "diagnosedIssue"
        );
      }

      return !edit.allFilled("issueDescription", "repairStatus");
    };

    const handleDeleteDocument = (document, index) => {
      const handleModalClose = () =>
        setConfirmModalConfig((prev) => ({ ...prev, open: false }));

      setConfirmModalConfig({
        open: true,
        title: "Delete Document",
        titleIcon: {
          path: mdi[ICONS_WEB.TRASH_CAN],
          color: theme.palette.error.main,
        },
        textPositive: BUTTON_LABELS.DELETE,
        buttonPositiveColor: Colors.Error,
        description:
          "You are about to delete this document. Are you sure you want to continue?",
        handlePositive: () => {
          (isIssueImageViewer ? removeIssueImage : removeRepairImage)(
            document,
            index
          );
          handleModalClose();
        },
        handleNegative: handleModalClose,
        ...((selectedIssueResult || readOnly) && {
          hideNegative: true,
          buttonPositiveColor: Colors.Primary,
          textPositive: BUTTON_LABELS.OK,
          description: "Task attachments cannot be modified after billing.",
          handlePositive: handleModalClose,
        }),
      });
    };

    const onSubmit = async ({ hasModalOpen = false } = {}) => {
      if (!isLastOne && !selectedIssue) {
        setIssueIndex((prev) => prev + 1);
      }

      if (readOnly) {
        return;
      }

      if (selectedIssue && !hasModalOpen) {
        setConfirmModalConfig({
          open: true,
          title: "Save Changes",
          titleIcon: {
            path: mdi[ICONS_WEB.SAVE],
            color: theme.palette.primary.main,
          },
          textPositive: "Yes, Save",
          description:
            "You are about to save changes made to the task description. Are you sure you would like to continue?",
          handlePositive: () => onSubmit({ hasModalOpen: true }),
          handleNegative: () =>
            setConfirmModalConfig((prev) => ({ ...prev, open: false })),
        });

        return;
      }

      reviewIssue({
        issue: currentIssue,
        attemptId: attempt.id,
        attemptStatus: attempt.attemptStatus,
        assignmentId: attempt.assignment.id,
        userId: attempt.userId,
        repairStatusWasEdited: edit.edits.repairStatus !== undefined,
        diagnosedIssue: edit.edits.diagnosedIssue,
        orderId: attempt.orderId,
        ...edit.getValue(),
      });

      if (isLastOne && !selectedIssue) {
        setActiveScreen(SCREENS.REVIEW);
      }

      if (selectedIssue) {
        setSelectedIssue(null);
        setActiveScreen(SCREENS.MAIN);
        setConfirmModalConfig((prev) => ({ ...prev, open: false }));
        return;
      }

      const isCompleted = COMPLETED_STATUSES.includes(
        edit.edits.repairStatus ?? getTaskResolutionStatus(currentIssue)
      )
        ? 1
        : 0;

      const nextCompleteTaskCountValue = completedIssueCount + isCompleted;

      setCompletedIssueCount(nextCompleteTaskCountValue);

      if (isLastOne) {
        const needsReview = nextCompleteTaskCountValue < issues.length;

        setIsWoReviewed(needsReview);
        setActiveScreen(SCREENS.REVIEW);
        return;
      }

      setView(VIEWS.ISSUE_REVIEW);
    };

    const getNextButtonText = () => {
      // Handle non-closeout state
      if (selectedIssue) {
        return "Save Changes";
      }

      // Handle last issue during closeout
      if (isLastOne) {
        return "Review";
      }

      // Handle intermediate issues during closeout
      return `${readOnly ? "Issue" : "Start: Closeout"} ${
        issueIndex + 2
      }/${issueCount}`;
    };

    useEffect(() => {
      getIssueTemplates();
    }, [attempt]);

    const historyEdit = useEdit({
      ...selectedIssueResult,
      issueDescription: selectedIssueResult?.description,
      repairDetails: selectedIssueResult?.repairDescription,
      repairStatus: selectedIssueResult
        ? getTaskResolutionStatus(selectedIssueResult)
        : undefined,
    });

    const {
      resolvingDocuments: selectedIssueResultResolvingDocuments = [],
      originatedDocuments: selectedIssueResultOriginatedDocuments = [],
    } = filterIssueFiles(selectedIssueResult?.documents || []);

    return (
      <>
        <Box className={classes.contentContainer}>
          {!selectedIssue &&
            (attempt.previousAttemptId ||
              attempt?.nextAttemptIds?.length > 0) && (
              <IssueHistoryHeader
                isCurrentAttempt={false}
                issueResult={selectedIssueResult}
                currentIssue={issueWithHistory}
                navigate={setSelectedIssueResult}
                attempt={attempt}
                lastUpdatedDate={issueWithHistory?.modifiedOn}
                attemptNumber={attempt?.attemptNumber}
              />
            )}

          <Box className={classes.content}>
            {selectedIssue && (
              <Box className={classes.header}>
                <Box className={classes.headerLeftBox}>Editing: Task</Box>
                <Box className={classes.headerRightBox}>
                  Task Last Updated:{" "}
                  {toFormattedDate(
                    currentIssue?.modifiedOn,
                    DATE_TIME_FORMATS.FORMAT_MMM_D_YYYY_H_MM_A
                  )}
                </Box>
              </Box>
            )}

            <Box style={{ padding: theme.spacing(1, 3) }}>
              {!selectedIssue && (
                <IssueUsersHeader
                  title={
                    <>
                      {issueIndex + 1}.&nbsp;
                      {`Closeout: ${title}`}
                    </>
                  }
                  attempt={attempt}
                  issue={currentIssue}
                  users={users}
                />
              )}
              {selectedIssueResult && historyEdit && (
                <IssueReviewContent
                  disabled
                  description={description}
                  edit={historyEdit}
                  isTask={isTask}
                  getFields={getFields}
                  sections={SECTIONS}
                  issueImages={selectedIssueResultOriginatedDocuments}
                  repairImages={selectedIssueResultResolvingDocuments}
                  setShowImagesViewer={setShowImagesViewer}
                  setImagesInViewer={setImagesInViewer}
                  onShowIssueImageViewer={turnOnIssueImageViewer}
                  onShowRepairImageViewer={turnOffIssueImageViewer}
                  handleAddIssueImage={() => {}}
                  handleAddRepairImage={() => {}}
                />
              )}

              {view === VIEWS.ISSUE_REVIEW && !selectedIssueResult && (
                <Box>
                  {selectedIssue && (
                    <MiDetailFields
                      detailFields={getFields({
                        section: SECTIONS.TASK_DETAILS,
                      })}
                      edit={edit}
                    />
                  )}
                  <IssueReviewContent
                    disabled={readOnly}
                    description={
                      currentIssue?.issueResult?.originalDescription ||
                      description
                    }
                    edit={edit}
                    isTask={isTask}
                    getFields={getFields}
                    sections={SECTIONS}
                    issueImages={issueImages}
                    repairImages={repairImages}
                    setShowImagesViewer={setShowImagesViewer}
                    setImagesInViewer={setImagesInViewer}
                    handleAddIssueImage={handleAddIssueImage}
                    handleAddRepairImage={handleAddRepairImage}
                    onShowIssueImageViewer={turnOnIssueImageViewer}
                    onShowRepairImageViewer={turnOffIssueImageViewer}
                    fullEditMode={!!selectedIssue}
                  />
                </Box>
              )}
            </Box>
          </Box>
          <Box className={classes.footer}>
            <MiButton
              className={classes.cancelBtn}
              title="Cancel"
              variant={BUTTON_VARIANTS.TEXT}
              onClick={onCancel}
              style={{
                textDecoration: "underline",
                textUnderlineOffset: theme.spacing(1),
              }}
            />
            {issueIndex !== 0 && (
              <MiButton
                title={`Return: Closeout ${issueIndex}/${issueCount}`}
                variant={BUTTON_VARIANTS.OUTLINED}
                className={classes.confirmBtn}
                onClick={() => setIssueIndex((prev) => prev - 1)}
              />
            )}
            {(!readOnly || (readOnly && !isLastOne)) && (
              <MiButton
                title={getNextButtonText()}
                disabled={isFormValid() || isLoading}
                variant={BUTTON_VARIANTS.CONTAINED}
                className={classes.confirmBtn}
                onClick={onSubmit}
              />
            )}
          </Box>
        </Box>
        <DocumentViewer
          documents={imagesInViewer}
          onUnfullscreen={() => {
            setShowImagesViewer(false);
          }}
          fullScreenStatus={showImagesViewer}
          onDelete={handleDeleteDocument}
        />
        <MiModalConfirm
          {...confirmModalConfig}
          sx={{ zIndex: theme.zIndex.popper }}
        />
      </>
    );
  }
);

const useStyles = makeStyles()((theme, { history = true }) => ({
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  header: {
    display: "flex",
  },
  headerLeftBox: {
    backgroundColor: theme.palette.red.main,
    flex: 1,
    padding: theme.spacing(2),
    color: theme.palette.common.white,
  },
  headerRightBox: {
    backgroundColor: theme.palette.orange.main,
    flex: 1,
    padding: theme.spacing(2),
    color: theme.palette.common.white,
  },
  content: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    backgroundColor: theme.palette.grey.light,
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: history
      ? theme.spacing(0, 0, 0.5, 0.5)
      : theme.shape.borderRadius,
  },
  footer: {
    backgroundColor: theme.palette.common.white,
    position: "sticky",
    bottom: 0,
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(4),
    padding: theme.spacing(2, 0),
    zIndex: theme.zIndex.drawer,
  },
  cancelBtn: {
    color: theme.palette.common.black,
    fontWeight: theme.typography.fontWeightBold,
  },
  confirmBtn: {
    fontWeight: theme.typography.fontWeightBold,
  },
}));
